import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Filter, ListChecks, KanbanSquare, Sparkles } from "lucide-react";
import { agents } from "@/lib/mockData";
import { useStore } from "@/lib/store";
import { listTasks, updateTask as apiUpdateTask, createTask } from "@/lib/tasksApi";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import PillToggle from "@/components/common/PillToggle";
import HitlBanner from "./HitlBanner";
import TaskTable from "./TaskTable";
import TaskKanban from "./TaskKanban";
import TaskDrawer from "./TaskDrawer";
import ErrorInline from "@/components/common/ErrorInline";
import { toast } from "sonner";

/** Phase D: real tasks from /api/tasks. UI preserved. */
export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const setStoreTasks = useStore((s) => s.setTasksList);
  const [view, setView] = useState("list"); // "list" | "kanban"
  const [filter, setFilter] = useState("all");
  const [openTaskId, setOpenTaskId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const data = await listTasks();
      setTasks(data || []);
      setStoreTasks?.(data || []);
    } catch (err) {
      setLoadError(err?.response?.data?.detail || err.message || "Couldn't load tasks");
    } finally {
      setLoading(false);
    }
  }, [setStoreTasks]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Hash deep-link: /tasks#task=<id>
  useEffect(() => {
    const m = (location.hash || "").match(/task=([^&]+)/);
    if (!m) return;
    const id = decodeURIComponent(m[1]);
    if (tasks.some((t) => t.id === id)) {
      setOpenTaskId(id);
    }
  }, [location.hash, tasks]);

  // "Just generated from debate" banner: show if any source=debate task was
  // created in the last 5 minutes.
  const freshDebateTasks = useMemo(() => {
    const cutoff = Date.now() - 5 * 60 * 1000;
    return tasks.filter(
      (t) =>
        t.source === "debate" &&
        t.created_at &&
        new Date(t.created_at).getTime() > cutoff,
    );
  }, [tasks]);

  const openCount = useMemo(
    () => tasks.filter((t) => t.status !== "done").length,
    [tasks],
  );
  const humanCount = useMemo(
    () => tasks.filter((t) => t.owner === "HUMAN" && t.status !== "done").length,
    [tasks],
  );

  const filtered = useMemo(() => {
    if (filter === "all") return tasks;
    if (filter === "human") return tasks.filter((t) => t.owner === "HUMAN");
    if (filter === "mine") return tasks.filter((t) => t.owner !== "HUMAN");
    return tasks.filter((t) => t.owner === filter);
  }, [tasks, filter]);

  const openTask = tasks.find((t) => t.id === openTaskId) ?? null;

  const handleOpenFirstHuman = () => {
    const first = tasks.find((t) => t.owner === "HUMAN" && t.status !== "done");
    if (first) setOpenTaskId(first.id);
  };

  // Persist updates to server, then refetch.
  const handleUpdate = async (id, patch) => {
    // Optimistic
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      await apiUpdateTask(id, patch);
      refresh();
    } catch (err) {
      toast.error("Update failed", {
        description: err?.response?.data?.detail || err.message,
      });
      refresh();
    }
  };

  const handleNewTask = async () => {
    const title = window.prompt("Task title?");
    if (!title || !title.trim()) return;
    try {
      await createTask({ title: title.trim(), owner: "HUMAN", priority: "med" });
      toast.success("Task created");
      refresh();
    } catch (err) {
      toast.error("Create failed", {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const filterLabel =
    filter === "all"
      ? "All tasks"
      : filter === "human"
        ? "HUMAN only"
        : filter === "mine"
          ? "Agent-owned"
          : `By: ${agents.find((a) => a.id === filter)?.name ?? filter}`;

  const handleCloseDrawer = () => {
    setOpenTaskId(null);
    if (location.hash) {
      navigate(location.pathname + location.search, { replace: true });
    }
  };

  const scrollToFreshBanner = () => {
    // The banner is right above the table — scroll to top of table instead.
    const first = freshDebateTasks[0];
    if (first) setOpenTaskId(first.id);
  };

  return (
    <div data-testid="tasks-page" className="px-5 pb-16 pt-6 sm:px-8 sm:pt-8 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end md:gap-6"
      >
        <div className="min-w-0">
          <div className="eyebrow">Execution Queue</div>
          <h1 className="mt-2 text-[40px] font-extrabold leading-[1] tracking-tight text-zinc-900 sm:text-[56px] lg:text-[72px]">
            Tasks
          </h1>
          <p
            data-testid="tasks-subtitle"
            className="mt-2 text-[13px] text-zinc-500 sm:text-[14px]"
          >
            <span className="font-bold text-zinc-700">{openCount} open</span>
            <span className="mx-1.5 text-zinc-300">·</span>
            <span className="font-bold text-orange-700">
              {humanCount} require human action
            </span>
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:gap-3">
          <PillToggle
            testId="view-toggle"
            value={view}
            onChange={setView}
            options={[
              { value: "list", label: "List" },
              { value: "kanban", label: "Kanban" },
            ]}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="filter-trigger"
                type="button"
                className="inline-flex h-10 min-h-[40px] items-center gap-2 rounded-full border border-black/[0.04] bg-white px-3.5 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 md:h-9"
              >
                <Filter className="h-3.5 w-3.5" strokeWidth={2.4} />
                <span className="truncate max-w-[160px]">{filterLabel}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Filter</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={filter} onValueChange={setFilter}>
                <DropdownMenuRadioItem value="all" data-testid="filter-all">
                  All tasks
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="mine" data-testid="filter-mine">
                  Agent-owned
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="human" data-testid="filter-human">
                  HUMAN only
                </DropdownMenuRadioItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                  By agent
                </DropdownMenuLabel>
                {agents.map((a) => (
                  <DropdownMenuRadioItem
                    key={a.id}
                    value={a.id}
                    data-testid={`filter-agent-${a.id}`}
                  >
                    {a.name} · {a.role}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            data-testid="new-task-btn"
            onClick={handleNewTask}
            className="ml-auto inline-flex h-10 min-h-[40px] items-center gap-1.5 rounded-full bg-black px-4 text-[12.5px] font-semibold text-white transition-colors hover:bg-zinc-800 md:ml-0 md:h-9"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.6} />
            New task
          </button>
        </div>
      </motion.div>

      {freshDebateTasks.length > 0 && (
        <button
          type="button"
          data-testid="fresh-debate-banner"
          onClick={scrollToFreshBanner}
          className="mt-6 flex w-full items-center justify-between gap-3 rounded-2xl border border-violet-200 bg-violet-50/60 px-4 py-3 text-left transition-colors hover:bg-violet-50"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-500/15 text-violet-700">
              <Sparkles className="h-4 w-4" strokeWidth={2.4} />
            </span>
            <div>
              <div className="text-[13px] font-bold text-violet-900">
                {freshDebateTasks.length} task
                {freshDebateTasks.length === 1 ? "" : "s"} generated from your
                boardroom debate
              </div>
              <div className="text-[11.5px] text-violet-700/80">
                Click to jump into the first action item.
              </div>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-violet-700">View →</span>
        </button>
      )}

      <div className="mt-6">
        <HitlBanner count={humanCount} onClick={handleOpenFirstHuman} />
      </div>

      <div className="mt-7">
        {loading ? (
          <div
            data-testid="tasks-loading"
            className="card-soft rounded-[28px] p-10 text-center text-[13px] text-zinc-500"
          >
            Loading tasks…
          </div>
        ) : loadError ? (
          <ErrorInline message={loadError} onRetry={refresh} />
        ) : view === "list" ? (
          <TaskTable tasks={filtered} onOpen={(id) => setOpenTaskId(id)} />
        ) : (
          <TaskKanban tasks={filtered} onOpen={(id) => setOpenTaskId(id)} />
        )}
      </div>

      <TaskDrawer
        task={openTask}
        open={Boolean(openTaskId)}
        onOpenChange={(o) => {
          if (!o) handleCloseDrawer();
        }}
        onUpdate={handleUpdate}
      />

      <div className="mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.14em] text-zinc-400">
        <ListChecks className="h-3 w-3" />
        <span>{filtered.length} task(s) shown</span>
        {view === "kanban" && (
          <>
            <span className="text-zinc-300">·</span>
            <KanbanSquare className="h-3 w-3" />
            <span>4 columns</span>
          </>
        )}
      </div>
    </div>
  );
}
